import { useState, useEffect } from 'react';
import { expensesApi, EXPENSE_CATEGORIES } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export const ExpenseModal = ({ open, onClose, propertyId, expense, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    monthly_amount: '',
    notes: '',
  });

  useEffect(() => {
    if (expense) {
      setFormData({
        category: expense.category || '',
        monthly_amount: expense.monthly_amount?.toString() || '',
        notes: expense.notes || '',
      });
    } else {
      setFormData({
        category: '',
        monthly_amount: '',
        notes: '',
      });
    }
  }, [expense, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error('Please select an expense category');
      return;
    }

    if (!formData.monthly_amount || parseFloat(formData.monthly_amount) <= 0) {
      toast.error('Please enter a valid monthly amount');
      return;
    }

    setLoading(true);
    try {
      const data = {
        category: formData.category,
        monthly_amount: parseFloat(formData.monthly_amount),
        notes: formData.notes.trim(),
      };

      if (expense) {
        await expensesApi.update(expense.id, data);
        toast.success('Expense updated successfully');
      } else {
        await expensesApi.create({
          ...data,
          property_id: propertyId,
        });
        toast.success('Expense added successfully');
      }
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save expense';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {expense ? 'Edit Expense' : 'Add Monthly Expense'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Category */}
          <div>
            <Label htmlFor="category">Expense Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
              disabled={loading}
            >
              <SelectTrigger className="mt-1.5" data-testid="expense-category-select">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Monthly Amount */}
          <div>
            <Label htmlFor="monthlyAmount">Monthly Amount (£) *</Label>
            <Input
              id="monthlyAmount"
              type="number"
              step="0.01"
              min="0"
              data-testid="expense-amount-input"
              value={formData.monthly_amount}
              onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })}
              placeholder="0.00"
              className="mt-1.5"
              disabled={loading}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              data-testid="expense-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any notes about this expense..."
              className="mt-1.5 resize-none"
              rows={2}
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="expense-submit-btn"
              className="bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner"></span>
                  Saving...
                </span>
              ) : (
                expense ? 'Save Changes' : 'Add Expense'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseModal;
